/**
 * Login Page
 * Magic link authentication for all environments
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { signInMagicLink } from '~/lib/auth-client';
import { useAuth } from '~/contexts/AuthContext';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';

export default function Login() {
  const { isAuthenticated, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div>Loading...</div>
      </div>
    );
  }

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/projects" replace />;
  }

  // Magic link login
  const handleMagicLinkLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('Sending magic link to:', email);
      // Use absolute URL for callback so it redirects to dashboard, not API
      const callbackURL = `${window.location.origin}/projects`;
      await signInMagicLink({
        email,
        callbackURL,
      });
      console.log('Magic link sent successfully');
      setMagicLinkSent(true);
    } catch (err: any) {
      console.error('Magic link error:', err);
      setError(err.message || 'Failed to send login link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (magicLinkSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md p-8 space-y-6">
          <h1 className="text-2xl font-bold text-center">Check Your Email</h1>
          <div className="space-y-4">
            <p className="text-gray-600">
              We've sent a login link to <strong>{email}</strong>.
            </p>
            <p className="text-gray-600">
              Click the link in the email to sign in.
            </p>
            <p className="text-sm text-gray-500">
              The link expires in 5 minutes.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setMagicLinkSent(false)}
            className="w-full"
          >
            Back to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md p-8 space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-bold">Sign In to Snaptics</h1>
          <p className="text-sm text-gray-600">
            Enter your email to receive a login link
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleMagicLinkLogin} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="you@example.com"
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? 'Sending magic link...' : 'Continue with email'}
          </Button>
        </form>

        <p className="text-center text-sm text-gray-600">
          We'll send you a link to sign in securely without a password.
        </p>

        <p className="text-center text-sm text-gray-600">
          Don't have an account?{' '}
          <a href="/signup" className="text-blue-600 hover:underline">
            Sign up
          </a>
        </p>
      </Card>
    </div>
  );
}
